/** @format */

import {useMemo} from 'react';

import {format} from 'date-fns';

import {Trans, useTranslation} from 'react-i18next';

import {ATTRIBUTION_MODELS} from 'pages/Home/Project/Steps/Periods/components/KpiPlanForm/constants';

import {useGetCampaignStatisticsAnalyticsSettingsQuery} from '../../graphql/__generated__';
import {Root, FieldGroup, FieldLabel, FieldValue} from './styles';

const DEFAULT_REVENUE_GOAL_LABEL = 'Ecommerce: \u043F\u043E\u043A\u0443\u043F\u043A\u0430';

interface KpiSettingsBlockProps {
  companyId: string;
  startDate: Date;
  endDate: Date;
}

const KpiSettingsBlock = ({companyId, startDate, endDate}: KpiSettingsBlockProps) => {
  const {t} = useTranslation();

  const {data, loading, error} = useGetCampaignStatisticsAnalyticsSettingsQuery({
    variables: {companyId},
  });

  const kpiSettings = data?.currentUser?.company?.kpiSettings;
  const rawAnalyticGoals = data?.currentUser?.company?.analyticGoals;
  const analyticGoals = useMemo(() => rawAnalyticGoals ?? [], [rawAnalyticGoals]);

  const attributionModelLabel = useMemo(() => {
    const model = kpiSettings?.attributionModel ?? 'AUTOMATIC';
    const found = ATTRIBUTION_MODELS.find(m => m.value === model);
    return found ? found.label : model;
  }, [kpiSettings?.attributionModel]);

  const goalsLabel = useMemo(() => {
    const goalIds = kpiSettings?.goals ?? [];
    if (goalIds.length === 0) return '\u2014';

    const names = goalIds
      .map(goalId => {
        const goal = analyticGoals.find(g => g.id === goalId);
        return goal ? goal.name : `ID: ${goalId}`;
      })
      .join(', ');

    return names;
  }, [kpiSettings?.goals, analyticGoals]);

  const revenueGoalLabel = useMemo(() => {
    const revenueGoalId = kpiSettings?.revenueGoalId ?? null;

    if (revenueGoalId === null) {
      return DEFAULT_REVENUE_GOAL_LABEL;
    }

    const goal = analyticGoals.find(g => g.id === revenueGoalId);
    if (goal) {
      return goal.name;
    }

    return `ID: ${revenueGoalId} (${t('\u0446\u0435\u043B\u044C \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430', '\u0446\u0435\u043B\u044C \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430')})`;
  }, [kpiSettings?.revenueGoalId, analyticGoals, t]);

  if (loading || error) {
    return null;
  }

  const periodLabel = `${format(startDate, 'dd.MM.yyyy')} \u2014 ${format(endDate, 'dd.MM.yyyy')}`;

  return (
    <Root>
      <FieldGroup>
        <FieldLabel>
          <Trans>Период</Trans>
        </FieldLabel>
        <FieldValue>{periodLabel}</FieldValue>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>
          <Trans>Модель атрибуции</Trans>
        </FieldLabel>
        <FieldValue>{attributionModelLabel}</FieldValue>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>
          <Trans>Цели лидов</Trans>
        </FieldLabel>
        <FieldValue>{goalsLabel}</FieldValue>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>
          <Trans>Цель дохода</Trans>
        </FieldLabel>
        <FieldValue>{revenueGoalLabel}</FieldValue>
      </FieldGroup>
    </Root>
  );
};

export {KpiSettingsBlock};
